import { type ProjectListItem } from '@sdk';
import { useProjectList } from './use-claude-projects';

interface UseAllProjectsOptions {
  /** When false, the underlying scan is skipped. */
  enabled?: boolean;
  /** When true, also include SDK-shipped system projects (Flowpad Assistant etc.). */
  includeSystem?: boolean;
  /** Only this workspace's projects (`useActiveWorkspace().scopeId`) — for a picker,
   *  not a lookup. Undefined lists every project. */
  workspace?: string;
}

/**
 * Unified project list for any picker. THIN wrapper over the backend's
 * ``list_projects_from_indexer`` (served via ``listProjectsFromComputeNode``),
 * which is itself a thin wrapper over ``get_all_projects`` — the single source
 * of truth that already merges:
 *
 *   1. Claude scan   (~/.claude/projects/<encoded>/)
 *   2. Codex scan    (~/.codex/config.toml + rollout JSONLs)
 *   3. Copilot scan  (~/.copilot/session-state workspace files)
 *   4. Project entity table (flowpad-registered projects)
 *
 * deduped by canonical posix cwd. No client-side merge needed anymore —
 * previous versions of this hook unioned three separate sources because the
 * backend lacked a single function for it.
 */
export function useAllProjects({
  enabled = true,
  includeSystem = false,
  workspace,
}: UseAllProjectsOptions = {}) {
  const { projects, isLoading } = useProjectList({ enabled, workspace });
  const filtered: ProjectListItem[] = includeSystem
    ? projects
    : projects.filter((p) => !p.hidden);
  return { projects: filtered, isLoading };
}
